/**
 * Chamadas HTTP dos orçamentos.
 *
 * Papel: aqui ficam as cinco chamadas de `/api/orcamentos` que a tela usa —
 * listar, criar, alterar, excluir e buscar a imagem de um item.
 *
 * A rota de obter um orçamento existe na API e não tem chamada aqui: a listagem
 * já traz os orçamentos inteiros, com os itens aninhados, e uma segunda busca
 * pelo mesmo dado só acrescentaria uma requisição.
 *
 * Como usar: a listagem devolve o array inteiro com os itens aninhados, **e sem
 * as imagens**. O que cada item traz é `temFoto`; a imagem vem por
 * `buscarFotoDoItem`, uma por vez e só quando alguém a abre.
 */
import {
  type NovoOrcamento,
  type Orcamento,
  fotoDoItemOrcamentoSchema,
  orcamentoSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeOrcamentosSchema = z.array(orcamentoSchema);

/**
 * Lê os orçamentos, do mais recente para o mais antigo.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os orçamentos com os itens aninhados, sem as imagens.
 */
export function buscarOrcamentos(sinal?: AbortSignal): Promise<Orcamento[]> {
  return clienteApi.get('/orcamentos', { schema: listaDeOrcamentosSchema, sinal });
}

/**
 * Lê a imagem de um item.
 *
 * É a única chamada que traz imagem, e por isso a única que pode custar
 * megabytes. Quem a dispara é a abertura da foto, e nunca a montagem de lista.
 *
 * @param itemId - Identificador da linha em `itens_orcamento`.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns A imagem em base64, ou `null` quando o item não tem nenhuma.
 */
export function buscarFotoDoItem(
  itemId: string,
  sinal?: AbortSignal,
): Promise<{ foto: string | null }> {
  return clienteApi.get(`/orcamentos/itens/${encodeURIComponent(itemId)}/foto`, {
    schema: fotoDoItemOrcamentoSchema,
    sinal,
  });
}

/**
 * Cria um orçamento com os itens dele.
 *
 * @param dados - O orçamento preenchido, já validado.
 * @returns O orçamento como ficou gravado.
 */
export function criarOrcamento(dados: NovoOrcamento): Promise<Orcamento> {
  return clienteApi.post('/orcamentos', dados, { schema: orcamentoSchema });
}

/**
 * Substitui todos os campos de um orçamento, e os itens dele por inteiro.
 *
 * Um item que não vier no corpo deixa de existir. A imagem de um item é o único
 * campo que sobrevive a não vir: ausente significa "não mexa", e string vazia é
 * o que apaga.
 *
 * @param id - Identificador da linha em `orcamentos`.
 * @param dados - O orçamento inteiro, e não apenas os campos alterados.
 * @returns O orçamento como ficou gravado.
 */
export function atualizarOrcamento(id: string, dados: NovoOrcamento): Promise<Orcamento> {
  return clienteApi.put(`/orcamentos/${encodeURIComponent(id)}`, dados, {
    schema: orcamentoSchema,
  });
}

/**
 * Apaga um orçamento, com os itens dele.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `orcamentos`.
 */
export function excluirOrcamento(id: string): Promise<void> {
  return clienteApi.remover(`/orcamentos/${encodeURIComponent(id)}`);
}
